<script setup lang="ts">
import {
  baseDoughnutDatasetSettings,
  getCSSVariableColor,
  retrieveClickedChartDatum,
  type ChartClickEventDatum,
  type DoughnutDataset,
  type MinersStatusChartConf,
} from '@/components/rc/work-order-dashboard/charts-helpers'
import type { MinerStatusStatistics } from '@/types/rc/work-orders'
import type { Matrix } from '@/types/shared/common'
import { useDark } from '@vueuse/core'
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip,
  type ActiveElement,
  type ChartData,
  type ChartEvent,
  type ChartOptions,
  type DoughnutControllerChartOptions,
} from 'chart.js'
import { max } from 'lodash'
import { computed } from 'vue'
import { Doughnut } from 'vue-chartjs'

const props = defineProps<{ totalMiners: number; stats: MinerStatusStatistics }>()

const emit = defineEmits<{ (event: 'arcClick', value: ChartClickEventDatum): void }>()

const isDark = useDark()

ChartJS.register(ArcElement, Tooltip, Legend)

// Extend with Controller-specific chart options
interface CustomDataset extends DoughnutDataset {
  radius: DoughnutControllerChartOptions['radius']
  cutout: DoughnutControllerChartOptions['cutout']
  label: MinersStatusChartConf['category']
}

// Chart labels. Determines how many elements per dataset are expected
const labels: MinersStatusChartConf['label'][] = ['Good', 'Fail', 'Unset']

type Matrix4x3 = Matrix<4, 3>

/** Source data, with as many (array) elements as there are datasets.
Each dataset should in turn have as many elems as there are chart labels. */
const sourceData = computed<Matrix4x3>(() => [
  [props.stats.inventoried, 0, max([props.totalMiners - props.stats.inventoried, 0])!],
  [props.stats.cleaned, 0, max([props.totalMiners - props.stats.cleaned, 0])!],
  [
    props.stats.diagnostic_passed,
    props.stats.diagnostic_fail,
    max([props.totalMiners - props.stats.diagnostic_passed - props.stats.diagnostic_fail, 0])!,
  ],
  [
    props.stats.stress_test_passed,
    props.stats.stress_test_fail,
    max([props.totalMiners - props.stats.stress_test_passed - props.stats.stress_test_fail])!,
  ],
])

/** Dataset settings, one per dataset expected */
const doughnutDatasetSettings: Omit<CustomDataset, 'data'>[] = [
  { label: 'Inventoried', radius: '100%', cutout: '20%' },
  { label: 'Cleaned', radius: '96%', cutout: '20%' },
  { label: 'Initial Diagnostic', radius: '90%', cutout: '20%' },
  { label: 'Stress Tested', radius: '82%', cutout: '20%' },
]

/** Base dataset. We'll lookup tooltip-displayed data here. */
const baseData = computed<DoughnutDataset[]>(() =>
  sourceData.value.map((data, i) => ({
    ...doughnutDatasetSettings[i],
    ...baseDoughnutDatasetSettings.value,
    backgroundColor: [
      getCSSVariableColor('--color-sprout-400'),
      getCSSVariableColor('--color-orange-500'),
      isDark.value ? getComputedStyle(document.documentElement).getPropertyValue('--component-fill-badge') : '#e2e2e2',
    ],
    data,
  }))
)

/** Full-blown chartData. Labels + zipped out datasets. */
const chartData = computed<ChartData<'doughnut', number[], string>>(() => ({
  labels,
  datasets: baseData.value,
}))

// Emit 'arcClick' so parent can filter out the table
const onClick = (event: ChartEvent, elements: ActiveElement[]) =>
  emit('arcClick', retrieveClickedChartDatum(chartData, elements))

/**
 * Chart-level options. Event handlers,
 */
const chartOptions = computed<ChartOptions<'doughnut'>>(() => ({
  responsive: true,
  maintainAspectRatio: false, // This fixes flashing at wrong dimensions
  onClick,
  plugins: {
    tooltip: {
      enabled: true,
      // Tooltip label callback
      callbacks: {
        title: (context) => {
          return context[0].dataset.label
        },
        label: (context) => {
          const value = context.raw as number
          // Total is sum of the dataset's datapoints for this one, not of cross-dataset category's datapoints as the others.
          const total = props.totalMiners
          const percentage = ((100 * value) / total).toFixed(0)
          if (value !== null) {
            return `${context.label}: ${value}/${total} ~ ${percentage}%`
          }
        },
      },
    },
    legend: {
      position: 'bottom',
      labels: {
        usePointStyle: true,
      },
    },
  },
}))
</script>

<template>
  <div class="w-[400px] h-[400px] flex items-center justify-center">
    <Doughnut :data="chartData" :options="chartOptions" />
  </div>
</template>
